import styled from 'styled-components';

import { device } from 'config/device';

export const TableNavWrapper = styled.div`
  position: relative;
  margin: 1rem 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  @media ${device.tablet} {
    gap: 1rem;
  }

  #records-count {
    opacity: 0.6;
    font-size: ${({ theme }) => theme.small};
  }

  /* Hide desktop records count on mobile */
  @media ${device.mobile} {
    #records-count {
      display: none;
    }
  }

  @media ${device.mobile} {
    position: sticky;
    top: 0;
    background-color: #fff;
    flex-direction: column;
    align-items: stretch;
  }
`;

export const ActionsWrapper = styled.div`
  grid-gap: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media ${device.tablet} {
    justify-content: space-around;
    &.approval-checkbox {
      flex: 1;
    }
  }

  @media ${device.mobile} {
    justify-content: flex-end;
    gap: 0.5rem; /* Reduce gap between buttons */

    /* When there are exactly 4 items, space them evenly across full width */
    &.four-items {
      justify-content: space-between;
      width: 100%;
      gap: 0.25rem; /* Reduce gap when using full width */
    }
  }

  .select-all-checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  /* Add consistent margins to all buttons */
  > * {
    margin-right: 10px;

    &:last-child {
      margin-right: 0;
    }

    @media ${device.mobile} {
      margin-right: 5px; /* Reduce margin on mobile */
    }
  }

  /* Hide New button on mobile - it will be shown in MobileTopRow */
  @media ${device.mobile} {
    .new-button {
      display: none;
    }

    /* Hide Excel and View buttons when they're shown in mobile top row */
    &.mobile-two-buttons {
      .excel-export,
      .column-filters {
        display: none;
      }
    }
  }
`;

type MobileTopRowProps = {
  $hasOnlyTwoButtons: boolean;
};

export const MobileTopRow = styled.div<MobileTopRowProps>`
  display: none;

  @media ${device.mobile} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${(props) => (props.$hasOnlyTwoButtons ? '0' : '0.5rem')};

    #records-count-mobile {
      opacity: 0.6;
      font-size: ${({ theme }) => theme.small};
    }

    .mobile-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      justify-content: flex-end;

      /* When there are exactly 4 items, space them evenly across full width */
      &:has(> *:nth-child(4):last-child) {
        justify-content: space-between;
        width: 100%;
        gap: 0.25rem; /* Reduce gap when using full width */
      }
    }

    /* Alternative approach: target specific class when 4 items are present */
    .mobile-actions.four-items {
      justify-content: space-between;
      width: 100%;
      gap: 0.25rem;
    }
  }
`;

type IconWrapperProps = {
  disabled: boolean;
};

export const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  align-items: center;
  height: 3rem;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  svg {
    fill: ${({ theme }) => theme.secondary};
    width: 100%;
    height: 100%;
    :hover {
      opacity: 0.6;
    }
  }
  .viewActionBtn {
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.special};
    padding: 4px 12px;
    border: 1px solid ${({ theme }) => theme.special};
    border-radius: 12px;
    svg {
      width: 25px;
      height: 15px;
      fill: ${({ theme }) => theme.special};
    }

    .view-text {
      margin-left: 8px;

      @media ${device.mobile} {
        display: none; /* Hide "View" text on mobile */
      }
    }
  }
`;
export const CheckMenuWrapper = styled.ul`
  position: absolute;
  list-style: none;
  z-index: 100;
  top: 5rem;
  right: 0rem;
  display: flex;
  flex-direction: column;
  width: max-content;
  border-radius: 5px;
  padding: 1.5rem 3rem;
  color: ${({ theme }) => theme.secondary};
  font-size: ${({ theme }) => theme.small};
  background: ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadow[4]};

  /* On mobile the "View" button is near the right edge, so open the menu
     toward the left to keep it fully on screen. */
  @media ${device.mobile} {
    right: auto;
    left: 0rem;
    max-width: calc(100vw - 3rem);
    padding: 1.5rem 2rem;
  }

  span {
    opacity: 1;
    margin-bottom: 1rem;
  }
  li {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    :last-child {
      margin-bottom: 0rem;
    }
  }

  li.orderByBlock {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid ${({ theme }) => theme.grayLight3 || '#d4d4d4'};

    span {
      margin-bottom: 0;
    }

    .orderBySelect {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid ${({ theme }) => theme.grayLight3 || '#d3d3d3'};
      border-radius: 6px;
      background: ${({ theme }) => theme.white};
      color: ${({ theme }) => theme.secondary};
      font-size: ${({ theme }) => theme.small};
    }

    .orderByDirection {
      display: flex;
      gap: 0.5rem;

      button {
        flex: 1;
        padding: 0.4rem 0;
        border: 1px solid ${({ theme }) => theme.special};
        border-radius: 6px;
        background: ${({ theme }) => theme.white};
        color: ${({ theme }) => theme.special};
        font-size: ${({ theme }) => theme.small};
        cursor: pointer;

        &.active {
          background: ${({ theme }) => theme.special};
          color: ${({ theme }) => theme.white};
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
`;
